import { Alert } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { Textarea } from '@ordo/ui/textarea'
import { Page } from 'compositions/Page'
import { useAppConfig, useDeleteAppConfig, useUpdateAppConfig } from 'hooks/api'
import { getApiErrorMessage, useFetchApi } from 'hooks/useFetchApi'
import { AlertCircle, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

const PROMPT_KEY = 'BROADCAST_DRAFTER_PROMPT'

interface DrafterStatus {
  available: boolean
  model: string
  /** The prompt shipped in code. Shown when no override is saved. */
  defaultPrompt: string
  hasOverride: boolean
}

/**
 * Admin → AI. One knob: the system prompt the broadcast drafter runs on.
 *
 * The default lives in code (`server/src/managers/broadcast-drafter.ts`) and
 * ships with each deploy. Saving here writes an AppConfig override that wins
 * until it's reset. Growth can retune tone without waiting for a release; the
 * Reset button always gets back to the reviewed baseline.
 */
export function AdminAi() {
  const { data: config, loading, refetch } = useAppConfig()
  const { perform: updateConfig, loading: saving } = useUpdateAppConfig()
  const { perform: deleteConfig, loading: resetting } = useDeleteAppConfig()
  const { fetchApi } = useFetchApi()

  const [status, setStatus] = useState<DrafterStatus | null>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Availability is informational only — a failure here shouldn't block
  // editing the prompt, so it degrades to "unknown" rather than an error.
  const fetchStatus = useCallback(() => {
    fetchApi<DrafterStatus>('/api/scheduled-communications/draft')
      .then(setStatus)
      .catch(() => setStatus(null))
  }, [fetchApi])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const stored = config?.[PROMPT_KEY]
  const hasOverride = Boolean(stored?.isSet)

  useEffect(() => {
    // Show the real prompt, never an empty box: the saved override when there
    // is one, otherwise the default that ships with the deploy. Deliberately
    // NOT persisted on load — writing the default into the DB would freeze it
    // and silently ignore future deploys. Pressing Save is what pins it.
    // `masked` carries the real value here (plaintext key, not a redaction).
    setDraft(stored?.masked ?? status?.defaultPrompt ?? '')
  }, [stored?.masked, status?.defaultPrompt])

  const handleSave = async () => {
    setError(null)
    setSaved(false)
    try {
      await updateConfig({ configs: [{ key: PROMPT_KEY, value: draft }] })
      setSaved(true)
      refetch()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the prompt'))
    }
  }

  const handleReset = async () => {
    setError(null)
    setSaved(false)
    try {
      await deleteConfig(PROMPT_KEY)
      // Restore the shipped text rather than blanking the editor.
      setDraft(status?.defaultPrompt ?? '')
      refetch()
      fetchStatus()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not reset the prompt'))
    }
  }

  return (
    <Page>
      <h1 className="mb-6 text-xl font-semibold">AI</h1>

      {error && (
        <Alert variant="error" className="mb-4 flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-sm">{error}</p>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Broadcast drafter</CardTitle>
          {status && (
            <Badge variant={status.available ? 'secondary' : 'outline'}>
              {status.available ? status.model : 'Not configured'}
            </Badge>
          )}
        </CardHeader>
        <Separator />
        <CardContent className="space-y-4 pt-6">
          <p className="text-sm text-muted-foreground">
            The system prompt used when someone drafts a broadcast from a description. Leave this empty to use the
            default that ships with the deploy.
          </p>

          {!status?.available && (
            <Alert variant="warning" className="text-sm">
              The AI gateway (9Router) isn&rsquo;t configured on this deployment, so drafting is unavailable and the
              &ldquo;Draft with AI&rdquo; entry points are hidden. Editing the prompt here is safe but has no effect
              until it is.
            </Alert>
          )}

          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <Textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value)
                setSaved(false)
              }}
              rows={18}
              className="font-mono text-xs"
              placeholder="Loading the default prompt…"
              aria-label="Broadcast drafter system prompt"
            />
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {hasOverride ? 'Using a saved override.' : 'Using the built-in default.'}
              {saved && ' Saved.'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset} disabled={!hasOverride || resetting || saving}>
                <RotateCcw className="mr-1 h-4 w-4" />
                Reset to default
              </Button>
              <Button onClick={handleSave} disabled={saving || resetting || draft.trim().length === 0}>
                Save prompt
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  )
}
